import React, { useState, useCallback } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import { CardHeading, Fieldset } from "components"
import { Modal } from "components"

import { Formik, Form, FieldArray } from "formik"

import ReactTooltip from "react-tooltip"

import * as yup from "yup"

import formatDate from "utils/formatDate"
import { bytesToSize } from "utils"

const DOCUMENTS = gql`
  query Trip($id: ID!) {
    trip(id: $id) {
      documents {
        id
        url
        name
        timestamp
        size
        type
      }
    }
  }
`

const CREATE_TRIP_DOCUMENTS = gql`
  mutation CreateTripDocuments($input: CreateTripDocumentsInput!) {
    createTripDocuments(input: $input) {
      id
      url
      name
      timestamp
      size
      type
    }
  }
`

const UPDATE_TRIP_DOCUMENT = gql`
  mutation UpdateTripDocument($input: DocumentInput!) {
    updateTripDocument(input: $input) {
      id
      url
      name
      timestamp
      size
      type
    }
  }
`

const DELETE_TRIP_DOCUMENT = gql`
  mutation DeleteTripDocument($input: DeleteTripDocumentInput!) {
    deleteTripDocument(input: $input) {
      id
    }
  }
`

const DocumentsContainer = ({ tripId }) => {
  const { data, loading, error } = useQuery(DOCUMENTS, {
    variables: { id: tripId },
  })
  const documents = data?.trip?.documents

  const [createTripDocuments] = useMutation(CREATE_TRIP_DOCUMENTS)
  const [updateTripDocument] = useMutation(UPDATE_TRIP_DOCUMENT)
  const [deleteTripDocument] = useMutation(DELETE_TRIP_DOCUMENT)

  const handleCreate = async (documents) => {
    try {
      const input = { tripId, documents }

      await createTripDocuments({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: DOCUMENTS,
            variables: { id: tripId },
          })

          cache.writeQuery({
            query: DOCUMENTS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                documents: [
                  ...existing?.trip?.documents,
                  ...data?.createTripDocuments,
                ],
              },
            },
          })
        },
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const handleUpdate = async (id) => {
    try {
      const input = { tripId, id }

      await updateTripDocument({
        variables: { input },
        update(cache, { data }) {
          const existing = cache.readQuery({
            query: DOCUMENTS,
            variables: { id: tripId },
          })

          const coupons = existing?.trip?.documents
          const updateCoupons = data?.updateTripDocuments
          cache.writeQuery({
            query: DOCUMENTS,
            variables: { id: tripId },
            data: {
              trip: {
                ...existing?.trip,
                documents: [
                  ...coupons.map((item) => {
                    const isUpdate = updateCoupons.find((_) => _.id === item.id)
                    return isUpdate ? isUpdate : item
                  }),
                ],
              },
            },
          })
        },
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        const input = { tripId, id }

        await deleteTripDocument({
          variables: { input },
          update(cache, { data }) {
            const existing = cache.readQuery({
              query: DOCUMENTS,
              variables: { id: tripId },
            })

            const documents = existing?.trip?.documents
            const deleted = data?.deleteTripDocument
            cache.writeQuery({
              query: DOCUMENTS,
              variables: { id: tripId },
              data: {
                trip: {
                  ...existing?.trip,
                  documents: {
                    documents: documents.filter((_) => _?.id === deleted?.id),
                  },
                },
              },
            })
          },
        })
      } catch (error) {
        console.error(error)
        throw error
      }
    }
  }

  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setIsEditing(false)
  }, [setIsEditing])

  const isEmpty = !data || documents?.length === 0

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {isEmpty ? (
        <>
          <CardHeading title="Documents" />
          <DocumentsEmpty isLoading={loading} onClick={openModal} />
        </>
      ) : (
        <>
          <CardHeading
            title="Documents"
            ctaTitle="Add Documents"
            onClick={openModal}
          />
          <DocumentsTable
            data={documents}
            // onEdit={(passenger) => {
            //   setSelectedPassenger(passenger)
            //   openModal()
            // }}
            onDelete={handleDelete}
          />
        </>
      )}

      <Modal title="Documents" isOpen={isEditing} onClose={closeModal}>
        <div className="max-w-screen-lg mx-auto">
          <DocumentsForm
            data={documents}
            onClose={closeModal}
            onSubmit={handleCreate}
          />
        </div>
      </Modal>
    </div>
  )
}

export default DocumentsContainer

//
const DocumentsEmpty = ({ isLoading, onClick }) => {
  return (
    <div className="py-16  flex flex-col justify-center items-center">
      {isLoading ? (
        <div>...Loading...</div>
      ) : (
        <>
          <p className="text-center">No Documents</p>

          <button
            onClick={onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add Document
          </button>
        </>
      )}
    </div>
  )
}

const DocumentsTable = ({ data, onSelect, onEdit, onDelete }) => {
  const header = ["Name", "Timestamp", "Size", ""]

  return (
    <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
      <table className="min-w-full table-auto">
        <thead>
          <tr>
            {header.map((item, index) => (
              <th
                key={index}
                className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                // style={{ position: "sticky", top: "0" }}
              >
                {item}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white">
          {data?.map((document, index) => (
            <tr
              onClick={(e) => {
                if (e.target.id !== "button") {
                  window.open(document.url, "_blank").focus()
                }
              }}
              key={index}
              className="hover:bg-blue-200 hover:text-blue-800 cursor-pointer"
            >
              <td className="w-6/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                <p className="text-sm leading-5 font-medium text-gray-900 pr-4">
                  {document.name}
                </p>
              </td>

              <td className="w-3/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200">
                <p className="text-sm leading-5 text-gray-500">
                  {formatDate(document.timestamp, "dd, MMMM yyyy HH:mm")}
                </p>
              </td>

              <td className="w-1/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 text-gray-500">
                <p className="text-sm leading-5 text-gray-500">
                  {bytesToSize(document.size)}
                </p>
              </td>

              <td
                onClick={(e) => e.stopPropagation()}
                className="w-1/12 px-6 py-4 whitespace-no-wrap border-b border-gray-200 text-sm leading-5 font-medium"
              >
                <div data-for="action" data-tip="hello world">
                  <svg
                    id="button"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                    />
                  </svg>
                </div>

                <ReactTooltip
                  event="click"
                  // eventOff="click"
                  globalEventOff="click"
                  isCapture={true}
                  id="action"
                  place="bottom"
                  type="light"
                  effect="solid"
                  clickable={true}
                  getContent={() => {
                    return (
                      <ul>
                        {/* <li>
                          <button
                            type="button"
                            className="text-blue-500"
                            // onClick={() => onEdit(passenger)}
                          >
                            Edit
                          </button>
                        </li> */}
                        <li>
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-800"
                            onClick={() => onDelete(document.id)}
                          >
                            Delete
                          </button>
                        </li>
                      </ul>
                    )
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const DocumentsForm = ({ onClose, onSubmit }) => {
  const formikProps = {
    initialValues: {
      documents: [],
    },
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit: async (values, actions) => {
      try {
        //This is expensive because everytime this functions gets called update will happen
        //TODO detect if values chaged, and then update shallowDeep(inital, current)

        actions.setSubmitting(true)

        await onSubmit(values?.documents)
        onClose()

        // const initialDataIds = data?.map((_) => _.id)
        // const currentDataIds = values?.documents?.map((_) => _.id)

        // const documents = values?.documents

        // const documentsForCreate = documents.filter((_) => !_.id)
        // const documentsForUpdate = documents.filter(
        //   (_) => _.id && initialDataIds?.includes(_.id),
        // )
        // const documentsForDelete = data?.filter(
        //   (_) => _.id && !currentDataIds?.includes(_.id),
        // )

        // if (documentsForCreate?.length > 0) {
        //   await onCreate(documentsForCreate)
        // }

        // if (documentsForUpdate?.length > 0) {
        //   await onUpdate(documentsForUpdate)
        // }

        // if (documentsForDelete?.length > 0) {
        //   await onDelete(documentsForDelete)
        // }
      } catch (e) {
        actions.setSubmitting(false)
        console.error("UPDATE TRIP INFROMATION ERROR", e)
        // const errors = e.graphQLErrors.map(v => v)
        // setFieldError("server", errors)
      }
    },
  }

  return (
    <Formik {...formikProps}>
      {({ errors }) => (
        <Form>
          <Fieldset component="dropzone" name="documents" tags={false} />
          <ModalNavigation {...{ onClose }} />
        </Form>
      )}
    </Formik>
  )
}

const ModalNavigation = ({ onClose }) => {
  return (
    <div className=" px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-red-500 focus:outline-none focus:border-red-700 focus:shadow-outline-red transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Save
        </button>
      </span>

      <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          onClick={onClose}
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Cancel
        </button>
      </span>
    </div>
  )
}
